/**
 * Page id + title for routes AxonPageContext's hand-written list does not name.
 *
 * Without this every Agent Atlas, Security, Endpoint Security and Code Security
 * page fell through to { id: pathname, title: 'Current Page' }, so the Saf3AI
 * Agent greeted "You're on Current Page".
 *
 * 1. A sidebar page from the nav catalog (longest matching path wins, so a
 *    detail page like /agent-atlas/users/:email reads as "Users"), titled with
 *    the same nav string the sidebar shows.
 * 2. Otherwise a name built from the URL: the product's label from
 *    productConfig plus the last readable path segment, e.g.
 *    /endpoint-security/security/signals -> "Endpoint Security Signals".
 */
import i18n from '../i18n';
import { NAV_PAGES } from '../config/nav/catalog';
import { PRODUCTS, detectProductFromPath } from '../components/layout/Sidebar/productConfig';

// Words a plain title-case would get wrong.
const ACRONYMS = {
  ai: 'AI', bom: 'BOM', roi: 'ROI', dlp: 'DLP', mcp: 'MCP', edr: 'EDR', mdm: 'MDM',
  sso: 'SSO', sast: 'SAST', sca: 'SCA', api: 'API', finops: 'FinOps', v2: '',
};

// Products whose pages already carry their own name: prefixing them would read
// "Agent Atlas Users". Custom Agents routes are named in the hand-written list.
const UNPREFIXED_PRODUCTS = new Set(['agent-atlas', 'custom-agents']);

// The module root segment of each prefixed product, dropped from the page name.
const PRODUCT_ROOTS = new Set(['endpoint-security', 'shadow-ai', 'sast', 'cloud', 'saas-ai', 'copilot', 'gemini']);

const NAV_ENTRIES = Object.entries(NAV_PAGES)
  // labelKey read per call: a flagged page's label follows the user's flags (catalog flaggedLabel)
  .map(([key, p]) => ({ id: key.replace(/\./g, '-'), to: p.to, get labelKey() { return p.labelKey; } }))
  .sort((a, b) => b.to.length - a.to.length);

// Record ids, emails, scan ids: anything with a digit, an @ or a long hex run.
const isIdSegment = (seg) => /\d|@|^[0-9a-f-]{16,}$/i.test(seg) || seg.startsWith(':');

export const humanizeSegment = (seg) => seg
  .split('-')
  .map((w) => (w.toLowerCase() in ACRONYMS ? ACRONYMS[w.toLowerCase()] : w.charAt(0).toUpperCase() + w.slice(1)))
  .filter(Boolean)
  .join(' ');

export const navPageMeta = (pathname) => {
  const path = (pathname || '').replace(/\/+$/, '') || '/';

  const nav = NAV_ENTRIES.find((e) => path === e.to || path.startsWith(`${e.to}/`));
  if (nav) {
    const fallback = humanizeSegment(nav.to.split('/').pop());
    return { id: nav.id, title: i18n.t(nav.labelKey, { ns: 'nav', defaultValue: fallback }) };
  }

  const segments = path.split('/').filter(Boolean).filter((s) => !isIdSegment(s));
  if (segments.length === 0) return null;

  const productId = detectProductFromPath(path);
  const product = productId && !UNPREFIXED_PRODUCTS.has(productId)
    ? PRODUCTS.find((p) => p.id === productId)
    : null;
  const named = product && PRODUCT_ROOTS.has(segments[0]) ? segments.slice(1) : segments;
  const name = named.length ? humanizeSegment(named[named.length - 1]) : 'Overview';
  const title = product ? `${product.label} ${name}` : (name || 'Overview');
  return { id: segments.join('-'), title };
};
